import QuizStatusBadge from 'components/badge/QuizStatusBadge';
import IndicatorScreen from 'components/indicator/IndicatorScreen';
import { DivConst } from 'constants/divConst';
import { AppHelper } from 'helpers/appHelper';
import { QuizHelper } from 'helpers/quizHelper';
import { useApiState } from 'hooks/useApiState';
import { useEffect, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { useNavigate, useParams } from 'react-router-dom';
import { QuizRespondentRefResponseType } from 'types/api/quizRespondentRefResponseType';
import WsModalReportDownload from './component/modal/WsModalReportDownload';

/**
 *　獲得リスト詳細画面
 */
export default function WsRespondentQuiz(): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 型定義 */
  /* ----------------------------------------------------------------------- */

  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const { showBoundary } = useErrorBoundary();
  const navigate = useNavigate();
  const { quizCd } = useParams();

  const { postQuizRespondentRef } = useApiState();

  const [processing, setProcessing] = useState(false);
  const [pageNoState, setPageNoState] = useState(1);
  const [quizState, setQuizState] = useState<QuizRespondentRefResponseType>(null);
  const [hasNextState, setHasNextState] = useState(false);

  const [modalReportDownloadOpenState, setModalReportDownloadOpenState] = useState(false);

  const pageLimit = 30;

  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */
  useEffect(() => {
    // 認証済の場合は、会社登録画面に遷移
    (async () => {
      try {
        await _search(1);
      } catch (e) {
        showBoundary(e);
      }
    })();
  }, []);

  /* ----------------------------------------------------------------------- */
  /* イベントハンドラ関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   *　レポート出力ボタン押下時の処理
   */
  const onhModalReportDownloadOpen = () => {
    setModalReportDownloadOpenState(true);
  };

  /**
   * ページングの前へボタン押下時の処理
   */
  const onPrev = async () => {
    if (pageNoState <= 1) {
      return;
    }
    _search(pageNoState - 1);
  };

  /**
   * ページングの前へボタン押下時の処理
   */
  const onNext = async () => {
    if (!hasNextState) {
      return;
    }
    _search(pageNoState + 1);
  };

  /* ----------------------------------------------------------------------- */
  /* プライベート関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * クイズ情報を検索します。
   */
  const _search = async (pageNo: number) => {
    // ローディングの表示
    setProcessing(true);

    // クイズ検索APIの呼び出し
    const response = await postQuizRespondentRef({
      quizCd: quizCd,
      limit: pageLimit,
      offset: (pageNo - 1) * pageLimit,
    });
    // 検索結果の設定
    setQuizState(response);
    setPageNoState(pageNo);

    setHasNextState(pageNo * pageLimit < response.respondentCount);

    // ローディングの停止
    setProcessing(false);
  };

  const _getPageStartNum = () => {
    return pageNoState * pageLimit - pageLimit + 1;
  };

  const _getPageEndtNum = () => {
    return hasNextState ? pageNoState * pageLimit : quizState.respondentCount;
  };

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <>
      {/* ローディング中の表示 */}
      <IndicatorScreen visible={processing} />

      {AppHelper.isNotEmpty(quizState) && (
        <>
          <section className="section-header user-detail">
            <div className="container">
              <a onClick={() => navigate(-1)} className="btn-back-list hover">
                戻る
              </a>

              <div className="card card-detail">
                <div className="card-body">
                  <div className="d-md-flex d-flex-detail">
                    <div className="flex-shrink-0 flex-left">
                      <h2 className="flex-title">{quizState.title}</h2>
                      <ul className="list-unstyled list-inline">
                        <li className="list-inline-item">
                          クイズ作成日：
                          {AppHelper.formatTimeToDate(quizState.createdAt, 'yyyy/MM/dd')}
                          <br className="d-md-none" />
                          公開期間：
                          {QuizHelper.formatPublishTerm({
                            quizStatusDiv: quizState.quizStatusDiv,
                            publishStartAt: quizState.publishStartAt,
                            publishEndAt: quizState.publishEndAt,
                            publishedAt: quizState.publishedAt,
                            terminatedAt: quizState.terminatedAt,
                          })}
                        </li>
                        <li className="list-inline-item">
                          <QuizStatusBadge quizStatusDiv={quizState.quizStatusDiv} />
                        </li>

                        <li className="list-inline-item">
                          リスト数：
                          {`${AppHelper.formatNumberToCurrency(quizState.respondentCount)}件`}
                        </li>
                      </ul>
                    </div>
                    <div className="flex-grow-1 flex-right">
                      <a
                        onClick={onhModalReportDownloadOpen}
                        className="btn btn-common btn-common-w238 moderate-blue"
                      >
                        リスト出力
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="section-user-detail">
            <div className="container">
              <div className="table-container table-responsive js-scrollable">
                <table className="table table-user">
                  <thead>
                    <tr>
                      <th className="col-2">
                        <p className="mb-0">メールアドレス</p>
                      </th>
                      <th className="col-2">回答日時</th>
                      <th className="col-2">正解数</th>
                      <th className="col-2">名前</th>
                      <th className="col-2">性別</th>
                      <th className="col-2">生年月日</th>
                      <th className="col-2">電話番号</th>
                      <th className="col-2">居住エリア</th>
                      <th className="col-2">自由項目1</th>
                      <th className="col-2">自由項目2</th>
                      <th className="col-2">自由項目3</th>
                    </tr>
                  </thead>

                  <tbody>
                    {quizState.respondents.map((respondent, index) => {
                      return (
                        <tr key={`ws-respondent-quiz-${index}`} className="respondent-table-row">
                          <td>
                            <div>{respondent.mailAddress}</div>
                          </td>
                          <td className="respondent-table-col">
                            <div>
                              {AppHelper.formatTimeToDate(respondent.createdAt, 'yyyy/MM/dd HH:mm')}
                            </div>
                          </td>
                          <td className="respondent-table-col">
                            <div>{respondent.correctCount}</div>
                          </td>
                          <td className="respondent-table-col">
                            <div>{respondent.respondentName}</div>
                          </td>
                          <td className="respondent-table-col">
                            <div>{AppHelper.getDivLabel('gender', respondent.genderDiv)}</div>
                          </td>
                          <td className="respondent-table-col">
                            <div>
                              {respondent.birthDate > 0 &&
                                AppHelper.formatTimeToDate(respondent.birthDate, 'yyyy/MM/dd')}
                            </div>
                          </td>
                          <td className="respondent-table-col">
                            <div>{respondent.phoneNumber}</div>
                          </td>
                          <td className="respondent-table-col">
                            <div>
                              {AppHelper.getDivLabel('prefecture', respondent.prefectureDiv)}
                            </div>
                          </td>
                          <td className="respondent-table-col">
                            <div className="respondent-table-cell">
                              {respondent.respondentCustomFieldAnswer01}
                            </div>
                          </td>
                          <td className="respondent-table-col">
                            <div className="respondent-table-cell">
                              {respondent.respondentCustomFieldAnswer02}
                            </div>
                          </td>
                          <td className="respondent-table-col">
                            <div className="respondent-table-cell">
                              {respondent.respondentCustomFieldAnswer03}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* ページングボタン */}
              <div className="d-flex justify-content-between align-items-center">
                <p className="page-number mb-0">{`Showing ${_getPageStartNum()}-${_getPageEndtNum()} of ${
                  quizState.respondentCount
                }`}</p>
                <nav aria-label="Page navigation" className="navigation">
                  <ul className="pagination mb-0">
                    <li className="page-item">
                      <a
                        onClick={onPrev}
                        className={`page-link prev ${pageNoState <= 1 && `disabled`}`}
                        aria-label="Previous"
                      >
                        <span aria-hidden="true"></span>
                      </a>
                    </li>
                    <li className="page-item">
                      <a
                        onClick={onNext}
                        className={`page-link next ${!hasNextState && `disabled`}`}
                        aria-label="Next"
                      >
                        <span aria-hidden="true"></span>
                      </a>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </section>
        </>
      )}
      {/* レポートダウンロードモーダル */}
      <WsModalReportDownload
        open={modalReportDownloadOpenState}
        setOpen={setModalReportDownloadOpenState}
        cd={quizCd}
        contentsType={DivConst.items.contentsType.quiz.value}
        reportDateFrom={null}
        reportDateTo={null}
      />
    </>
  );
}
